interface OptimizelyState {
  getExperimentStates?: (options: {
    isActive: boolean;
  }) => Record<string, ExperimentState>;
}

interface ExperimentState {
  variation?: {
    id: string;
    name?: string;
  };
}

interface ExperimentData {
  experiments?: Record<
    string,
    {
      name?: string;
    }
  >;
}

export function getOptimizelyExperimentData() {
  "use strict";

  if (!window.optimizely || typeof window.optimizely.get !== "function") {
    console.warn("Optimizely object not available on window.");
    return;
  }

  // Force Optimizely to update state for the current page
  if (window.optimizely.push) {
    // First, deactivate all experiments
    window.optimizely.push({
      type: "deactivate",
    });

    // Then activate for the current page
    window.optimizely.push({
      type: "activate",
    });
  }

  const state = window.optimizely?.get("state") as OptimizelyState | undefined;
  const data = window.optimizely?.get("data") as ExperimentData | undefined;

  if (!state || !data) {
    console.warn("Optimizely state/data not available.");
    return;
  }

  // 1. Get all active experiment states for this page
  const experimentStates = state.getExperimentStates
    ? state.getExperimentStates({ isActive: true })
    : null;

  if (!experimentStates || Object.keys(experimentStates).length === 0) {
    console.warn("No active experiments found for this page.");
    return;
  }

  // 2. Build a list of experiments with names + variation names
  const experimentsSeen = Object.keys(experimentStates).map((expId) => {
    const expState = experimentStates[expId];
    const expData = data.experiments && data.experiments[expId];

    const experimentName =
      expData && expData.name ? expData.name : "Experiment " + expId;

    const variationName =
      expState &&
      expState.variation &&
      (expState.variation.name || expState.variation.id);

    return {
      experimentId: expId,
      experimentName: experimentName,
      variationId: expState?.variation && expState?.variation.id,
      variationName: variationName,
    };
  });

  if (!experimentsSeen.length) {
    console.warn("Experiment states exist but no variations found.");
    return;
  }

  // 3. Read existing cookie value
  function getExistingCookieValue(): string {
    const match = document.cookie.match(/SWI_AB_Test=([^;]+)/);
    if (match) {
      try {
        return decodeURIComponent(match[1] ?? "");
      } catch (e) {
        console.warn("Failed to decode existing SWI_AB_Test cookie:", e);
        return "";
      }
    }
    return "";
  }

  const existingValue = getExistingCookieValue();

  // 4. Format current experiments as "Campaign1|Experience1;Campaign2|Experience2"
  const newExperiments = experimentsSeen
    .filter((exp) => exp.experimentName && exp.variationName)
    .map((exp) => exp.experimentName + "|" + exp.variationName);

  // 5. Filter out duplicates from newExperiments if existingValue exists
  let cookieValue: string;
  if (!newExperiments.length) {
    console.warn("No valid experiments to add");
    return;
  }

  if (!existingValue) {
    cookieValue = newExperiments.join(";");
  } else {
    const existingExperiments = existingValue.split(";");

    const uniqueNewExperiments = newExperiments.filter(
      (newExp) => existingExperiments.indexOf(newExp) === -1,
    );

    if (uniqueNewExperiments.length === 0) {
      return;
    }

    cookieValue = existingValue + ";" + uniqueNewExperiments.join(";");
  }

  // 6. Set session cookie
  const cookieString =
    "SWI_AB_Test=" +
    encodeURIComponent(cookieValue) +
    "; path=/" +
    "; SameSite=None" +
    "; Secure";

  document.cookie = cookieString;
}
